import { Component, OnInit, inject, input, output, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { collection, doc, getDoc, getDocs, query, updateDoc, where, setDoc, DocumentData, QuerySnapshot } from '@angular/fire/firestore';
import { createUserWithEmailAndPassword, sendEmailVerification, updateProfile } from '@angular/fire/auth';
import { getDownloadURL, ref, uploadBytesResumable } from '@angular/fire/storage';
import { passwordMatchValidator } from '../../validators/password-match.validator';
import { FIREBASE_AUTH, FIREBASE_STORAGE, FIRESTORE } from '../../../fireauth-lib.config';
import { BlogPostStoreService } from '../../services/blog-post-store.service';
import { lastValueFrom } from 'rxjs';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'lib-accept-invite',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './accept-invite.component.html',
  styleUrls: ['./accept-invite.component.css']
})
export class AcceptInviteComponent implements OnInit {
  
  tokenReceived = input.required<string>();
  formSubmitted = output<{ email: string | null; displayName: string | null; photoUrl: string | null; emailVerified: boolean }>();
  signInClicked = output<void>()

  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private auth = inject(FIREBASE_AUTH); // ✅ Inject Firebase Auth from library
  private firestore = inject(FIRESTORE);
  private storage = inject(FIREBASE_STORAGE)
  blogPostStoreService = inject(BlogPostStoreService)
  userService = inject(AuthService)

  // Signals for reactive UI updates
  tokenParam = signal<string>('');
  inviteExists = signal<boolean>(false);
  inviteAccepted = signal<boolean>(false);
  inviteEmail = signal<string>('');
  inviteRole = signal<string>('');
  errorMessage = signal<string>('');
  showCancelMessage = signal<boolean>(false);
  inviteDocId = signal<string>('');
  existingUser = signal<boolean>(false)
  registrationComplete = signal<boolean>(false)

  // Form-related properties
  registrationForm!: FormGroup;
  photoPreview: string | null = null;
  selectedFile: File | null = null;

  ngOnInit(): void {
    this.existingUser.set(false)
    this.initForm();
    this.getTokenFromURL();
  }

  private initForm(): void {
    this.registrationForm = this.fb.group({
      displayName: ['', Validators.required],
      firstName: ['', Validators.required],  // Added firstName field
      lastName: ['', Validators.required],   // Added lastName field
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
      aboutMe: ['']
    }, {
      validators: passwordMatchValidator('password', 'confirmPassword')
    });
  }

  private getTokenFromURL(): void {
    this.route.queryParams.subscribe(params => {
      const token = params['token'];
      if (token) {
        this.tokenParam.set(token);
        this.checkInviteToken(token);
      } else {
        this.errorMessage.set('Invalid invitation link. Please check your email again.');
      }
    });
  }

  async checkInviteToken(token: string): Promise<void> {
    try {
      console.log('Token:', token);
      // Change: Query by token field instead of document ID
      const invitesRef = collection(this.firestore, 'InvitedUsers');
      const q = query(invitesRef, where('token', '==', token));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        this.errorMessage.set('This invitation is invalid or has expired.');
        return;
      }
      
      // Get the first document that matches
      const inviteDoc = querySnapshot.docs[0];
      const inviteData = inviteDoc.data();
      
      // Save the document ID for later updates
      this.inviteDocId.set(inviteDoc.id);
      this.inviteExists.set(true);
      this.inviteEmail.set(inviteData['email']);
      this.inviteRole.set(inviteData['role']);
      this.registrationComplete.set(inviteData['registered'])

      
      if (inviteData['accepted']) {
        this.inviteAccepted.set(true);
      }
    } catch (error) {
      console.error('Error checking invitation:', error);
      this.errorMessage.set('An error occurred while checking your invitation. Please try again later.');
    }
  }

  async acceptInvite(): Promise<void> {
    try {
      // Mark the invitation as accepted
      const inviteRef = doc(this.firestore, `InvitedUsers/${this.inviteDocId()}`);
      const inviteDoc = await getDoc(inviteRef);
      const inviteData = inviteDoc.data();
      
      if (!inviteData) {
        throw new Error('Invitation data not found');
      }
      
      // Check if user already exists in Users collection
      const querySnapshot = await this.checkIfUserExists(this.inviteEmail());
      
      if (!querySnapshot.empty) {
        // User already exists, update their invitedOrgRole array
        await this.updateUserOrgRole(querySnapshot, inviteData);
        
        // Mark invitation as accepted
        await updateDoc(inviteRef, {
          accepted: true,
          registered: true // Also mark as registered since user already exists
        });
        this.existingUser.set(true)
        // Show success message or redirect
        this.errorMessage.set('Invitation accepted! You already have an account with this email.');
      } else {
        // User doesn't exist, proceed with standard flow
        await updateDoc(inviteRef, {
          accepted: true
        });
        this.inviteAccepted.set(true);
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      this.errorMessage.set('Failed to accept invitation. Please try again.');
    }
  }

  async updateUserOrgRole(querySnapshot: QuerySnapshot<DocumentData, DocumentData>, inviteData: DocumentData) {
    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();

    const organization = await lastValueFrom(this.blogPostStoreService.getOrganizationObservable(inviteData['orgCode']))

    // Prepare the new role entry
    const newRoleEntry = {
      orgCode: inviteData['orgCode'] || '',
      orgName: organization?.orgName,
      role: inviteData['role'].toLowerCase()
    };

    // Update the invitedOrgRole array
    let invitedOrgRoles = userData['invitedOrgRoles'] || [];

    // Check if this organization is already in the array
    const existingOrgIndex = invitedOrgRoles.findIndex((entry: any) => entry.orgCode === newRoleEntry.orgCode);

    if (existingOrgIndex >= 0) {
      // Update the existing entry
      invitedOrgRoles[existingOrgIndex] = newRoleEntry;
    } else {
      // Add a new entry
      invitedOrgRoles.push(newRoleEntry);
    }

    const orgCode = userData['orgCode'] ? userData['orgCode'] : inviteData['orgCode']
    //const roles = Array.from(new Set([inviteData['role'].toLowerCase(), ...userData['roles']]));
    
    console.log(`Existing invited user details:`)
    console.log(`orgCode: ${orgCode}`)
    //console.log(`roles: ${JSON.stringify(roles)}`)
    console.log(`invitedOrgRoles: ${JSON.stringify(invitedOrgRoles)}`)

    // Update the user document
    await updateDoc(doc(this.firestore, `Users/${userDoc.id}`), {
      orgCode,
      invitedOrgRoles
    });
  }

 async checkIfUserExists(invitedEmail: string) {
    const usersRef = collection(this.firestore, 'Users');
    const q = query(usersRef, where('email', '==', invitedEmail));
    const querySnapshot = await getDocs(q);
    return querySnapshot;
  }

  cancelRegistration(): void {
    this.inviteAccepted.set(false);
    this.showCancelMessage.set(true);
    setTimeout(() => this.showCancelMessage.set(false), 5000);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.previewImage();
    }
  }

  previewImage(): void {
    if (!this.selectedFile) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      this.photoPreview = reader.result as string;
    };
    reader.readAsDataURL(this.selectedFile);
  }

  async onSubmit(): Promise<void> {
    if (this.registrationForm.invalid) return;

    try {
      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(
        this.auth,
        this.inviteEmail(),
        this.registrationForm.value.password
      );

      const user = userCredential.user;
      let photoURL = '';

      // Upload profile photo if selected
      if (this.selectedFile) {
        photoURL = await this.uploadProfileImage(user.uid);
      }

      // Update user profile
      await updateProfile(user, {
        displayName: this.registrationForm.value.displayName,
        photoURL: photoURL
      });

      // Update user document in Firestore
      await this.createUserDocument(user.uid, photoURL);

      // Update invite document as completed
      await updateDoc(doc(this.firestore, `InvitedUsers/${this.inviteDocId()}`), {
        registered: true
      }).then(() => {
        console.log('Invite document updated');
        return sendEmailVerification(this.auth.currentUser!);       
      });
      this.formSubmitted.emit({ email: user.email, displayName: user.displayName, photoUrl: user.photoURL, emailVerified: user.emailVerified });
      this.registrationForm.reset();
      // Show success or redirect
      //window.location.href = '/login';
    } catch (error: any) {
      //this.formSubmitted.emit({ email: null, displayName: null, photoUrl: null, emailVerified: false });
      console.error('Registration error:', error);
      this.errorMessage.set(this.userService.getFriendlyErrorMessage(error) || 'Failed to create account. Please try again.');
    }
  }

  private async uploadProfileImage(userId: string): Promise<string> {
    if (!this.selectedFile) return '';

    try {
      const storageRef = ref(this.storage, `profile/${userId}/${Date.now()}_${this.selectedFile.name}`);
      const uploadTask = uploadBytesResumable(storageRef, this.selectedFile);
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          () => {}, // Progress
          (error) => reject(error),
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(downloadURL);
            } catch (err) {
              reject(err);
            }
          }
        );
      });
    } catch (error) {
      console.error('Error uploading profile image:', error);
      return '';
    }
  }

  private async createUserDocument(userId: string, photoURL: string): Promise<void> {
    const userDocRef = doc(this.firestore, `Users/${userId}`);
    const inviteDocRef = doc(this.firestore, `InvitedUsers/${this.inviteDocId()}`);
    const inviteDoc = await getDoc(inviteDocRef);
    const inviteData = inviteDoc.data();
    const organization = await lastValueFrom(this.blogPostStoreService.getOrganizationObservable(inviteData!['orgCode']))

    const invitedOrgRoles = {
      orgCode: inviteData ? inviteData['orgCode'] :  '',
      orgName: organization?.orgName,
      role: inviteData ? inviteData['role'].toLowerCase() : ''
    }

    // Use setDoc instead of updateDoc since this is a new user document
    await setDoc(userDocRef, {
      displayName: this.registrationForm.value.displayName,
      firstName: this.registrationForm.value.firstName,  // Added firstName
      lastName: this.registrationForm.value.lastName,    // Added lastName
      aboutMe: this.registrationForm.value.aboutMe || '',
      photoUrl: photoURL,
      roles: inviteData ? [inviteData['role'].toLowerCase()] : ['user'], // Store as array
      orgCode: inviteData ? inviteData['orgCode'] || '' : '',
      email: this.inviteEmail(),  // Added email
      createdAt: new Date(),
      invitedOrgRoles
    });
  }
  
  signIn() {
    console.log('Sign in clicked')
    this.signInClicked.emit()
  }

  // Form getters for template
  get displayName() { return this.registrationForm.get('displayName')!; }
  get firstName() { return this.registrationForm.get('firstName')!; }
  get lastName() { return this.registrationForm.get('lastName')!; }
  get password() { return this.registrationForm.get('password')!; }
  get confirmPassword() { return this.registrationForm.get('confirmPassword')!; }
}
